import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Grid
} from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line
} from 'recharts';
import { CHART_COLORS, COLORS, STAT_CARD_STYLES, glassCardSx, chartPaperSx, sectionTitleSx, tableHeaderSx, tableRowSx, overdueRowSx, alertInfoSx, tooltipPaperSx, makeXAxisLabel, makeYAxisLabel, CHART_MARGINS } from './clientPortalTheme';

import ClientPortalExpandableChart from './ClientPortalExpandableChart';

const PAPER_MIN_HEIGHT = 460;

/** Colourful — used inside chart series only */
const STATUS_COLORS = {
  SCHEDULED: '#1976d2',
  IN_PROGRESS: '#ed6c02',
  HOLD: '#9c27b0',
  PARTS_PROCURED: '#00897b',
  COMPLETED: '#2e7d32',
  CANCELLED: '#757575',
  UNKNOWN: '#9e9e9e'
};

const URGENCY_COLORS = {
  Overdue: '#c62828',
  'This week': '#ed6c02',
  'This month': '#f9a825',
  Later: '#1976d2',
  'No due date': '#bdbdbd'
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatMonth = (monthKey) => {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-');
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const startOfDay = (date = new Date()) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const priorityColor = (priority) => {
  switch (priority) {
    case 'CRITICAL':
    case 'HIGH':
      return 'error';
    case 'MEDIUM':
      return 'default';
    default:
      return 'default';
  }
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={4} sx={{ minWidth: 160, ...tooltipPaperSx }}>
      <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Box key={entry.name} display="flex" justifyContent="space-between" gap={2} sx={{ py: 0.25 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>{entry.name}</Typography>
          <Typography variant="caption" fontWeight={600}>{entry.value}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

const pieLabel = ({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`;

const chartMargins = CHART_MARGINS.default;
const chartMarginsVertical = CHART_MARGINS.vertical;
const axisTick = { fontSize: 12, fill: COLORS.textMuted };
const legendStyle = { fontSize: 13, paddingTop: 8, color: COLORS.textSecondary };
const denseAxisProps = (count) => (count > 8
  ? { angle: -35, textAnchor: 'end', height: 80, interval: count > 16 ? Math.floor(count / 12) : 0 }
  : { height: 56 });

const ClientMaintenancePanel = ({ maintenance, loading }) => {
  const analytics = useMemo(() => {
    const openWorkOrders = maintenance?.openWorkOrders || [];
    const upcomingItems = maintenance?.upcomingItems || [];
    const completedHistory = maintenance?.completedHistory || [];
    const today = startOfDay();
    const in7Days = new Date(today);
    in7Days.setDate(in7Days.getDate() + 7);
    const in30Days = new Date(today);
    in30Days.setDate(in30Days.getDate() + 30);
    const in90DaysAgo = new Date(today);
    in90DaysAgo.setDate(in90DaysAgo.getDate() - 90);

    const allActiveItems = [
      ...openWorkOrders.map((wo) => ({ ...wo, itemKind: 'workOrder' })),
      ...upcomingItems.map((item) => ({ ...item, itemKind: 'upcoming' }))
    ];

    const withDueDate = allActiveItems.filter((item) => item.dueDate);
    const overdue = withDueDate.filter((item) => startOfDay(new Date(item.dueDate)) < today);
    const dueThisWeek = withDueDate.filter((item) => {
      const d = startOfDay(new Date(item.dueDate));
      return d >= today && d <= in7Days;
    });
    const dueThisMonth = withDueDate.filter((item) => {
      const d = startOfDay(new Date(item.dueDate));
      return d > in7Days && d <= in30Days;
    });
    const dueLater = withDueDate.filter((item) => startOfDay(new Date(item.dueDate)) > in30Days);
    const noDueDate = allActiveItems.filter((item) => !item.dueDate);

    const criticalHigh = openWorkOrders.filter((wo) => ['CRITICAL', 'HIGH'].includes(wo.priority));
    const inProgress = openWorkOrders.filter((wo) => wo.status === 'IN_PROGRESS');
    const onHold = openWorkOrders.filter((wo) => wo.status === 'HOLD');
    const completedRecently = completedHistory.filter(
      (wo) => wo.completedDate && new Date(wo.completedDate) >= in90DaysAgo
    );

    const nextDue = [...withDueDate]
      .filter((item) => startOfDay(new Date(item.dueDate)) >= today)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0] || null;

    const statusChartData = Object.entries(
      openWorkOrders.reduce((acc, wo) => {
        const key = (wo.status || 'UNKNOWN').replace(/_/g, ' ');
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    ).map(([name, value]) => ({ name, value }));

    const priorityChartData = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']
      .map((priority) => ({
        priority,
        label: priority.charAt(0) + priority.slice(1).toLowerCase(),
        open: openWorkOrders.filter((wo) => (wo.priority || 'MEDIUM') === priority).length,
        upcoming: upcomingItems.filter((item) => (item.priority || 'MEDIUM') === priority).length
      }))
      .filter((row) => row.open > 0 || row.upcoming > 0);

    const urgencyChartData = [
      { name: 'Overdue', value: overdue.length },
      { name: 'This week', value: dueThisWeek.length },
      { name: 'This month', value: dueThisMonth.length },
      { name: 'Later', value: dueLater.length },
      { name: 'No due date', value: noDueDate.length }
    ].filter((row) => row.value > 0);

    const dueByMonth = {};
    const addDueItem = (item, bucket) => {
      if (!item.dueDate) return;
      const d = new Date(item.dueDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!dueByMonth[key]) {
        dueByMonth[key] = { month: key, openWO: 0, upcoming: 0, total: 0 };
      }
      dueByMonth[key][bucket] += 1;
      dueByMonth[key].total += 1;
    };
    openWorkOrders.forEach((wo) => addDueItem(wo, 'openWO'));
    upcomingItems.forEach((item) => addDueItem(item, 'upcoming'));

    const dueChartData = Object.values(dueByMonth).sort((a, b) => a.month.localeCompare(b.month));

    const completedByMonth = completedHistory.reduce((acc, wo) => {
      if (!wo.completedDate) return acc;
      const d = new Date(wo.completedDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const completionChartData = Object.entries(completedByMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, count]) => ({ month, count }));

    const typeChartData = Object.entries(
      openWorkOrders.reduce((acc, wo) => {
        const key = wo.type || 'Other';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {})
    )
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const insights = [];
    if (overdue.length > 0) {
      insights.push({
        tone: 'red',
        text: `${overdue.length} maintenance item${overdue.length > 1 ? 's are' : ' is'} overdue and require${overdue.length === 1 ? 's' : ''} immediate attention.`
      });
    }
    if (criticalHigh.length > 0) {
      insights.push({
        tone: 'red',
        text: `${criticalHigh.length} open work order${criticalHigh.length > 1 ? 's' : ''} marked critical or high priority.`
      });
    }
    if (dueThisWeek.length > 0) {
      insights.push({
        tone: 'indigo',
        text: `${dueThisWeek.length} item${dueThisWeek.length > 1 ? 's' : ''} due within the next 7 days — plan downtime accordingly.`
      });
    }
    if (inProgress.length > 0) {
      insights.push({
        tone: 'indigo',
        text: `${inProgress.length} work order${inProgress.length > 1 ? 's' : ''} currently in progress.`
      });
    }
    if (onHold.length > 0) {
      insights.push({
        tone: 'grey',
        text: `${onHold.length} work order${onHold.length > 1 ? 's' : ''} on hold — may block scheduled operations.`
      });
    }
    if (nextDue && overdue.length === 0) {
      insights.push({
        tone: 'indigo',
        text: `Next scheduled maintenance: ${formatDate(nextDue.dueDate)} — ${nextDue.title || 'Untitled'}.`
      });
    }
    if (completedRecently.length > 0 && insights.length < 4) {
      insights.push({
        tone: 'grey',
        text: `${completedRecently.length} work order${completedRecently.length > 1 ? 's' : ''} completed in the last 90 days.`
      });
    }

    return {
      openWorkOrders,
      upcomingItems,
      completedHistory,
      overdue,
      dueThisWeek,
      dueThisMonth,
      criticalHigh,
      inProgress,
      onHold,
      completedRecently,
      nextDue,
      statusChartData,
      priorityChartData,
      urgencyChartData,
      dueChartData,
      completionChartData,
      typeChartData,
      insights,
      totalActive: allActiveItems.length
    };
  }, [maintenance]);

  if (loading) return null;

  const hasData = analytics.totalActive > 0 || analytics.completedHistory.length > 0;

  if (!hasData) {
    return <Alert severity="info" sx={{ ...alertInfoSx }}>No maintenance records found for this aircraft.</Alert>;
  }

  const statCards = [
    {
      key: 'open',
      label: 'Open Work Orders',
      value: analytics.openWorkOrders.length,
      bg: STAT_CARD_STYLES.indigo
    },
    analytics.overdue.length > 0 && {
      key: 'overdue',
      label: 'Overdue',
      value: analytics.overdue.length,
      bg: STAT_CARD_STYLES.red
    },
    {
      key: 'dueWeek',
      label: 'Due This Week',
      value: analytics.dueThisWeek.length,
      bg: STAT_CARD_STYLES.redDark
    },
    {
      key: 'dueMonth',
      label: 'Due in 30 Days',
      value: analytics.dueThisWeek.length + analytics.dueThisMonth.length,
      bg: STAT_CARD_STYLES.indigoDark
    },
    analytics.criticalHigh.length > 0 && {
      key: 'critical',
      label: 'Critical / High',
      value: analytics.criticalHigh.length,
      bg: STAT_CARD_STYLES.red
    },
    {
      key: 'inProgress',
      label: 'In Progress',
      value: analytics.inProgress.length,
      bg: STAT_CARD_STYLES.indigoLight
    },
    {
      key: 'upcoming',
      label: 'Upcoming Items',
      value: analytics.upcomingItems.length,
      bg: STAT_CARD_STYLES.grey
    },
    {
      key: 'completed90',
      label: 'Completed (90 days)',
      value: analytics.completedRecently.length,
      bg: STAT_CARD_STYLES.greyLight
    }
  ].filter(Boolean);

  const chartPaperSxLocal = { ...chartPaperSx, minHeight: PAPER_MIN_HEIGHT };
  const hasCharts = analytics.statusChartData.length > 0
    || analytics.urgencyChartData.length > 0
    || analytics.dueChartData.length > 0
    || analytics.priorityChartData.length > 0
    || analytics.completionChartData.length > 0;

  const pieFooter = (
    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: COLORS.textMuted, mt: 1 }}>
      Slice size = number of open work orders · Labels show status and share (%)
    </Typography>
  );

  const renderUrgencyChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={analytics.urgencyChartData} layout="vertical" margin={chartMarginsVertical}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={axisTick} label={makeXAxisLabel('Number of Maintenance Items')} />
        <YAxis type="category" dataKey="name" tick={axisTick} width={expanded ? 120 : 100} label={makeYAxisLabel('Due-Date Urgency Bucket')} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="value" name="Items" radius={[0, 4, 4, 0]} maxBarSize={expanded ? 48 : 36}>
          {analytics.urgencyChartData.map((entry) => (
            <Cell key={entry.name} fill={URGENCY_COLORS[entry.name] || CHART_COLORS[0]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const renderStatusPieChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={analytics.statusChartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={expanded ? 200 : 120}
          label={pieLabel}
          labelLine
        >
          {analytics.statusChartData.map((entry) => (
            <Cell
              key={entry.name}
              fill={STATUS_COLORS[entry.name.replace(/ /g, '_').toUpperCase()] || CHART_COLORS[0]}
            />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={legendStyle} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderPriorityChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={analytics.priorityChartData} margin={chartMargins}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis dataKey="label" tick={axisTick} label={makeXAxisLabel('Priority Level')} />
        <YAxis allowDecimals={false} tick={axisTick} width={48} label={makeYAxisLabel('Number of Items')} />
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="open" name="Open WOs" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} maxBarSize={expanded ? 72 : 56} />
        <Bar dataKey="upcoming" name="Upcoming" fill={CHART_COLORS[3]} radius={[4, 4, 0, 0]} maxBarSize={expanded ? 72 : 56} />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderDueByMonthChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={analytics.dueChartData} margin={chartMargins}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis
          dataKey="month"
          tick={axisTick}
          tickFormatter={formatMonth}
          label={makeXAxisLabel('Calendar Month (Due Date)')}
          {...denseAxisProps(analytics.dueChartData.length)}
        />
        <YAxis allowDecimals={false} tick={axisTick} width={48} label={makeYAxisLabel('Number of Scheduled Items')} />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const point = payload[0]?.payload || {};
            return (
              <Paper elevation={4} sx={{ minWidth: 180, ...tooltipPaperSx }}>
                <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                  {formatMonth(label)}
                </Typography>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Open WOs</Typography>
                  <Typography variant="caption" fontWeight={600}>{point.openWO || 0}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Upcoming</Typography>
                  <Typography variant="caption" fontWeight={600}>{point.upcoming || 0}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Total</Typography>
                  <Typography variant="caption" fontWeight={600}>{point.total || 0}</Typography>
                </Box>
              </Paper>
            );
          }}
        />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="openWO" stackId="due" name="Open WOs" fill={CHART_COLORS[0]} radius={[0, 0, 0, 0]} maxBarSize={expanded ? 64 : undefined} />
        <Bar dataKey="upcoming" stackId="due" name="Upcoming" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} maxBarSize={expanded ? 64 : undefined} />
        <Line type="monotone" dataKey="total" name="Total" stroke={CHART_COLORS[4]} strokeWidth={expanded ? 3 : 2} dot={{ r: expanded ? 5 : 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderCompletionChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={analytics.completionChartData} margin={chartMargins}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} />
        <XAxis
          dataKey="month"
          tick={axisTick}
          tickFormatter={formatMonth}
          label={makeXAxisLabel('Calendar Month (Completion Date)')}
          {...denseAxisProps(analytics.completionChartData.length)}
        />
        <YAxis allowDecimals={false} tick={axisTick} width={48} label={makeYAxisLabel('Work Orders Completed (count)')} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="count" name="Completed" fill={CHART_COLORS[9]} radius={[4, 4, 0, 0]} maxBarSize={expanded ? 64 : 48} />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderTypeChart = ({ height, expanded }) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={analytics.typeChartData} layout="vertical" margin={chartMarginsVertical}>
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.glassBorder} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={axisTick} label={makeXAxisLabel('Number of Open Work Orders')} />
        <YAxis type="category" dataKey="name" tick={axisTick} width={expanded ? 130 : 110} label={makeYAxisLabel('Maintenance Type')} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="value" name="Count" fill={CHART_COLORS[5]} radius={[0, 4, 4, 0]} maxBarSize={expanded ? 44 : 32} />
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <Box>
      {analytics.insights.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {analytics.insights.map((insight, idx) => {
            const toneSx = insight.tone === 'red'
              ? { bgcolor: COLORS.redGlass, color: COLORS.text, border: `1px solid rgba(229, 57, 53, 0.35)` }
              : insight.tone === 'grey'
                ? { bgcolor: COLORS.greyGlass, color: COLORS.textSecondary, border: `1px solid ${COLORS.glassBorder}` }
                : { bgcolor: COLORS.indigoGlass, color: COLORS.textSecondary, border: `1px solid rgba(42, 95, 143, 0.3)` };
            return (
              <Alert
                key={idx}
                icon={false}
                sx={{ borderRadius: 2, ...toneSx, '& .MuiAlert-message': { color: 'inherit' } }}
              >
                {insight.text}
              </Alert>
            );
          })}
        </Box>
      )}

      {statCards.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 4,
                  background: card.bg,
                  color: COLORS.white,
                  boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', top: -20, right: -10 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</Typography>
                <Typography variant="h5" fontWeight={800}>{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {hasCharts && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {analytics.urgencyChartData.length > 0 && (
            <Grid item xs={12} md={6}>
              <ClientPortalExpandableChart
                title="Due-Date Urgency"
                description="Action timeline — overdue items need immediate scheduling"
                paperSx={chartPaperSxLocal}
              >
                {renderUrgencyChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}

          {analytics.statusChartData.length > 0 && (
            <Grid item xs={12} md={6}>
              <ClientPortalExpandableChart
                title="Open Work Orders by Status"
                description={`Current workload breakdown across ${analytics.openWorkOrders.length} open orders`}
                footer={pieFooter}
                paperSx={chartPaperSxLocal}
              >
                {renderStatusPieChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}

          {analytics.priorityChartData.length > 0 && (
            <Grid item xs={12}>
              <ClientPortalExpandableChart
                title="Priority Distribution"
                description="Open work orders vs upcoming items by priority — focus on critical and high first"
                paperSx={chartPaperSxLocal}
              >
                {renderPriorityChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}

          {analytics.dueChartData.length > 0 && (
            <Grid item xs={12}>
              <ClientPortalExpandableChart
                title="Maintenance Due by Month"
                description="Scheduled workload — plan aircraft availability around peak months"
                paperSx={chartPaperSxLocal}
              >
                {renderDueByMonthChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}

          {analytics.completionChartData.length > 0 && (
            <Grid item xs={12} md={analytics.typeChartData.length > 0 ? 6 : 12}>
              <ClientPortalExpandableChart
                title="Completion Trend"
                description="Work orders completed per month — track maintenance throughput"
                paperSx={chartPaperSxLocal}
              >
                {renderCompletionChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}

          {analytics.typeChartData.length > 0 && (
            <Grid item xs={12} md={analytics.completionChartData.length > 0 ? 6 : 12}>
              <ClientPortalExpandableChart
                title="Open Work Orders by Type"
                description="Maintenance category mix — identify dominant work types"
                paperSx={chartPaperSxLocal}
              >
                {renderTypeChart}
              </ClientPortalExpandableChart>
            </Grid>
          )}
        </Grid>
      )}

      {analytics.openWorkOrders.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={sectionTitleSx}>Open Work Orders</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ ...glassCardSx, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeaderSx}>
                  <TableCell>WO #</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Assigned To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.openWorkOrders.map((wo) => {
                  const isOverdue = wo.dueDate && startOfDay(new Date(wo.dueDate)) < startOfDay();
                  return (
                    <TableRow key={wo._id} sx={isOverdue ? overdueRowSx : tableRowSx}>
                      <TableCell>{wo.workOrderNumber || '—'}</TableCell>
                      <TableCell>{wo.title}</TableCell>
                      <TableCell>{wo.type}</TableCell>
                      <TableCell>
                        <Chip label={wo.status?.replace(/_/g, ' ')} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={wo.priority} size="small" color={priorityColor(wo.priority)} />
                      </TableCell>
                      <TableCell>
                        {formatDate(wo.dueDate)}
                        {isOverdue && (
                          <Chip label="Overdue" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {wo.assignedTo
                          ? `${wo.assignedTo.firstName || ''} ${wo.assignedTo.lastName || ''}`.trim()
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {analytics.upcomingItems.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: COLORS.text }}>Upcoming Maintenance Items</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ ...glassCardSx, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Remaining</TableCell>
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.upcomingItems.map((item, idx) => {
                  const isOverdue = item.dueDate && startOfDay(new Date(item.dueDate)) < startOfDay();
                  return (
                    <TableRow key={item.sourceId || idx} sx={isOverdue ? overdueRowSx : tableRowSx}>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.type}</TableCell>
                      <TableCell>
                        {formatDate(item.dueDate)}
                        {isOverdue && (
                          <Chip label="Overdue" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{item.remainingDisplay || '—'}</TableCell>
                      <TableCell>
                        <Chip label={item.priority || 'MEDIUM'} size="small" color={priorityColor(item.priority)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {analytics.completedHistory.length > 0 && (
        <Box>
          <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: COLORS.text }}>Maintenance History</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ ...glassCardSx, borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WO #</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.completedHistory.slice(0, 20).map((wo) => (
                  <TableRow key={wo._id}>
                    <TableCell>{wo.workOrderNumber || '—'}</TableCell>
                    <TableCell>{wo.title}</TableCell>
                    <TableCell>
                      <Chip label={wo.status} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(wo.completedDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default ClientMaintenancePanel;
